import React, { useState } from 'react'
import { Envelope, PencilLine, Trash } from '@phosphor-icons/react'
import { EmailTemplateResp } from 'types/globalTypes'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { capitalizeFirstLetter, convertToLocal } from 'helper/utility'
import { Checkbox } from 'antd'
import Chip from 'components/global/Chip'
import { creationClassStore } from 'state/class/classCreationStore'
import ClassPreview from 'pages/Events/Views/Common/ModalCotent/EmailTemplate/Preview/ClassPreview'
import { EmailTemplate } from 'types/Classes/EmailTemplates'

interface TemplateInfoProps {
  template: EmailTemplate
  loaderId: string
  isClassTemplate: boolean
  handleAddToClass: (id: string, operation: 'add' | 'remove') => void
  handleEditTemplate: (template: EmailTemplate) => void
}

const TemplateInfo: React.FC<TemplateInfoProps> = ({
  template,
  loaderId,
  isClassTemplate,
  handleAddToClass,
  handleEditTemplate,
}) => {
  const classData = creationClassStore(store => store.classData)
  const [openPreviewModal, setOpenPreviewModal] = useState<boolean>(false)

  const getAgeTagText = (minAge?: number, maxAge?: number) => {
    if (minAge === 0 && maxAge === 0) return 'All Ages'
    if (minAge && !maxAge) return `Ages ${minAge}+`
    if (!minAge && maxAge) return `Ages up to ${maxAge}`
    if (minAge && maxAge) return `Ages ${minAge}-${maxAge}`
    return 'All Ages'
  }

  // Function to get class ticket names by their IDs
  const getTicketNames = () => {
    const ticketNames: string[] = []
    
    // For classes, we might have different ticket types or pricing tiers
    if (template.recipientsToSend?.admissionTicketIds) {
      template.recipientsToSend.admissionTicketIds.forEach(id => {
        // Assuming classData has admissionTickets similar to events
        const ticket = classData.admissionTickets?.find(
          ticket => ticket._id === id,
        )
        if (ticket) {
          ticketNames.push(ticket.name)
        }
      })
    }

    if (template.recipientsToSend?.additionalOptionIds) {
      template.recipientsToSend.additionalOptionIds.forEach(id => {
        // Assuming classData has additionalOptions similar to events
        const option = classData.additionalOptions?.find(
          option => option._id === id,
        )
        if (option) {
          ticketNames.push(option.name)
        }
      })
    }

    if (template.recipientsToSend?.dropInTicketIds) {
      template.recipientsToSend.dropInTicketIds.forEach(id => {
        // Assuming classData has dropInTicket similar to events
        if (classData.dropInTicket && classData.dropInTicket._id === id) {
          ticketNames.push(classData.dropInTicket.name)
        }
      })
    }

    return ticketNames
  }

  const tickets = getTicketNames()

  const hanldeChange = (value: boolean) => {
    handleAddToClass(template._id!, value ? 'add' : 'remove')
  }

  return (
    <>
      <div className="py-15px border-btm flex align-center justify-between gap-40px">
        <div className="w-70p">
          {loaderId === template._id ? (
            <ButtonSpinner size={20} color="#2ba7ca" />
          ) : (
            <Checkbox
              checked={template.status === 'active'}
              onChange={e => hanldeChange(e.target.checked)}
            >
              <div>
                <h3 className="text-16 w-500">
                  {capitalizeFirstLetter(template.templateName || '')}
                </h3>
                {template.customTimeToSend ? (
                  <p>{`Send at ${convertToLocal(template.subject || '')}`}</p>
                ) : (
                  <p>{`Send ${template.subject || ''}`}</p>
                )}
                <div className="flex gap-10px align-center flex-wrap">
                  {template.recipientsToSend?.gender ? (
                    <Chip
                      text={capitalizeFirstLetter(
                        template.recipientsToSend.gender,
                      )}
                    />
                  ) : null}
                  <Chip
                    text={getAgeTagText(
                      template.recipientsToSend?.minAge,
                      template.recipientsToSend?.maxAge,
                    )}
                  />
                  {tickets.map((ticket, index) => {
                    return <Chip key={index} text={capitalizeFirstLetter(ticket)} />
                  })}
                </div>
              </div>
            </Checkbox>
          )}
        </div>
        <div className="flex gap-20px align-center">
          <button
            className="borderd-btn"
            onClick={() => setOpenPreviewModal(true)}
          >
            Preview
          </button>
          <button
            className="borderd-btn"
            onClick={() => handleEditTemplate(template)}
          >
            Edit
          </button>
        </div>

        <ClassPreview
          openModal={openPreviewModal}
          closeModal={setOpenPreviewModal}
          template={template}
        />
      </div>
    </>
  )
}

export default TemplateInfo
